/**
 * The message log (owner, 2026-10-10): one row per email or push we send, so the team site can show how many of each kind go out,
 * how many are held or fail, and how many are opened. One closed list of types and four channels: a send site that names
 * neither does not compile (services/api EmailMessage.type) and the test message-log-tags.test.ts scans every send site.
 * Rows hold no address, no name and no message text: a salted person hash, the business id, the device id, the type, the outcome,
 * the provider's error code and message id. Kept 90 days (MESSAGE_LOG_DAYS), then pruned (docs/DATA-MAP.md).
 */

export type MessageChannel = "email" | "ios" | "android" | "browser";
export const MESSAGE_CHANNELS: readonly MessageChannel[] = ["email", "ios", "android", "browser"];
export const MESSAGE_CHANNEL_LABEL: Record<MessageChannel, string> = { email: "Email", ios: "iPhone push", android: "Android push", browser: "Browser push" };

/** sent: handed to the provider. held_test: a test address, kept off the provider. held_budget: today's budget was used up. held_fatigue: the person already had six non-safety pushes in a day (core FATIGUE_MAX), logged once per item. failed: the provider or our code refused. */
export type MessageOutcome = "sent" | "held_test" | "held_budget" | "held_fatigue" | "failed";
export const MESSAGE_OUTCOMES: readonly MessageOutcome[] = ["sent", "held_test", "held_budget", "held_fatigue", "failed"];

export const MESSAGE_TYPES = {
  signin_code: "Sign-in code",
  transfer_code: "Ownership code",
  staff_code: "Team site code",
  invite: "Invite or sign-in link",
  ownership_offer: "Ownership offer",
  reminder: "Check or task reminder",
  escalation: "Unopened task email",
  ticket_reply: "Support reply",
  staff_notice: "Team notice",
  answer_review: "Answers waiting (team)",
  comment_review: "Comments waiting (team)",
  comment_reply: "Reply to a site comment",
  bulk_message: "Message to customers",
  promo_message: "Offer to customers",
  digest: "Digest",
  prep_handoff: "Prep hand-off",
  waitlist: "Waitlist",
  lead_followup: "Lead follow-up",
  billing_notice: "Billing notice",
  org_notice: "Business notice",
  referral_notice: "Referral notice",
  review_invite: "Review invitation",
  feature_trial: "Feature trial note",
  winback: "Win-back offer",
  link_report: "Link report",
  cost_alert: "Cost flag",
  crash_alert: "Crash alert (team)",
  account_deleted: "Account deleted",
  food_roundup: "Food roundup",
  team_push: "Team push",
  notify_link: "Linked email notice",
  notify_digest: "Notification summary",
  guest_reply: "Reply to a guest note",
  place_watch: "Saved place inspected (push)",
} as const;
export type MessageType = keyof typeof MESSAGE_TYPES;
export const MESSAGE_TYPE_IDS = Object.keys(MESSAGE_TYPES) as MessageType[];
export const isMessageType = (s: unknown): s is MessageType => typeof s === "string" && Object.hasOwn(MESSAGE_TYPES, s);

/** Types a person can turn off in their own settings today (reach kinds and email notices); the rest are service mail. */
export const MESSAGE_LOG_DAYS = 90;

/** Postmark's free plan (owner: raise it with EMAIL_MONTHLY_LIMIT when the plan is upgraded). */
export const DEFAULT_EMAIL_MONTHLY_LIMIT = 100;
export const EMAIL_LIMIT_WARN_AT = 0.8;

/** One group of the log: what the report adds up. */
export interface MessageCount { day: string; channel: MessageChannel; type: MessageType; outcome: MessageOutcome; test: boolean; errorCode: string; n: number; opened: number; acted: number; openSecondsTotal: number; openSecondsN: number }

/** The platform of a push device row to a channel. A phone is iOS or Android by its provider (apns, fcm) or its platform. */
export function pushChannelOf(d: { channel: "phone" | "browser"; provider?: "expo" | "apns" | "fcm" | null; platform?: string | null }): MessageChannel {
  if (d.channel === "browser") return "browser";
  if (d.provider === "apns") return "ios";
  if (d.provider === "fcm") return "android";
  return /android/i.test(d.platform ?? "") ? "android" : "ios";
}

/** Median of seconds (the middle value; the mean of the two middle ones when even). Null with none. */
export function medianOf(xs: readonly number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b), m = s.length >> 1;
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** A send's log id as it travels in a link or push payload: 32 lowercase hex (128 random bits). It names a send, never a person. */
export const MESSAGE_ID_RE = /^[a-f0-9]{32}$/;
/** The id from a push payload (`data.mid`), or null. */
export const messageIdOf = (data: unknown): string | null => {
  const v = data && typeof data === "object" ? (data as { mid?: unknown }).mid : undefined;
  return typeof v === "string" && MESSAGE_ID_RE.test(v) ? v : null;
};
/** The id from a web address's query (`?mid=`), and the query without it. */
export function messageIdFromQuery(search: string): { id: string | null; rest: string } {
  const p = new URLSearchParams(search);
  const v = p.get("mid");
  p.delete("mid");
  const rest = p.toString();
  return { id: v && MESSAGE_ID_RE.test(v) ? v : null, rest: rest ? `?${rest}` : "" };
}
